/**
 * 출하 검사 성적서 — A4 한 장짜리 인쇄물.
 *
 * **읽기 전용이다.** 채우는 일은 편집 화면(`Inspection.tsx`)이 일반 웹 화면으로 하고, 이것은
 * 채운 내용을 종이 양식에 옮겨 그린다. 한때 A4 양식 위에서 바로 채웠는데, 210mm 폭에 맞춘
 * 좁은 칸과 작은 글자로 반나절 검사를 하기가 고역이었다. 대신 성적서를 따로 두면 "화면에서
 * 채운 것이 종이에 어떻게 나오는가" 를 인쇄 전에 볼 곳이 필요하다 — 그것이 이 화면이다.
 *
 * 인쇄하면 사이드바·조작 단추만 빠지고 나머지는 보이는 그대로다(`styles.css` 의 `@media print`).
 */
import type { ReactNode } from 'react';
import type { Display } from '../api/client.js';
import { formatSeconds } from '../components/ui.js';
import { INSPECTION_SECTIONS, SUMMARY_LABEL, itemNumber, type Summary, type Verdict } from '../inspection/checklist.js';
import type { ReportState } from '../inspection/report.js';
import {
  fleetLabel,
  issuersLabel,
  printedAt,
  readyLabel,
  resolutionOf,
  stateOf,
  versionSpread,
  type SystemSnapshot,
} from '../inspection/snapshot.js';

/** 판정 칸의 글자. 칸이 좁아 '해당 없음' 은 줄여 쓴다 — 범례를 성적서 머리에 둔다. */
const MARK_LABEL: Readonly<Record<Verdict, string>> = { pass: '합격', fail: '불합', na: 'N/A' };

export function InspectionReport({
  state,
  summary,
  snapshot,
}: {
  state: ReportState;
  summary: Summary;
  snapshot: SystemSnapshot;
}): ReactNode {
  const { header } = state;
  const rows = snapshot.displays;

  return (
    <article className="sheet">
      <header className="sheet-head">
        <div>
          <h1>출하 검사 성적서</h1>
          <p>SignageOne 디지털 사이니지 시스템 · Factory Acceptance Test Report</p>
        </div>
        <div className="sheet-docno">
          <span>문서번호</span>
          <Text value={header.docNo} className="sheet-docno-value" />
        </div>
      </header>

      <table className="sheet-meta">
        <tbody>
          <tr>
            <th>고객사</th>
            <td>
              <Text value={header.customer} />
            </td>
            <th>설치 현장</th>
            <td>
              <Text value={header.site} />
            </td>
          </tr>
          <tr>
            <th>납품 구성</th>
            <td>
              <Text value={header.system} />
            </td>
            <th>검사일</th>
            <td>
              <Text value={header.inspectedOn} />
            </td>
          </tr>
          <tr>
            <th>검사자</th>
            <td>
              <Text value={header.inspector} />
            </td>
            <th>고객 입회자</th>
            <td>
              <Text value={header.witness} />
            </td>
          </tr>
        </tbody>
      </table>

      <div className="sheet-verdict">
        <div>
          <span className="sheet-verdict-label">종합 판정</span>
          <strong className={`sheet-verdict-value is-${summary.verdict}`}>{SUMMARY_LABEL[summary.verdict]}</strong>
        </div>
        <p>
          전체 {summary.total}항목 · 합격 {summary.pass} · 불합격 {summary.fail} · 해당 없음 {summary.na} · 미판정{' '}
          {summary.pending}
          {summary.criticalFail > 0 ? ` · 필수 항목 불합격 ${summary.criticalFail}` : ''}
        </p>
        <p className="sheet-legend">
          판정 표기 — <b>합격</b>: 기준을 만족함 · <b>불합</b>: 기준 미달(비고에 사유 기재) · <b>N/A</b>: 이 납품에
          해당하지 않음. <b>필수</b> 표시 항목은 하나라도 불합격이면 출하할 수 없습니다.
        </p>
      </div>

      <Block title="검사 시점 시스템 현황" hint="검사 당시 서버가 보고한 값입니다.">
        <table className="sheet-table sheet-snapshot">
          <tbody>
            <tr>
              <th>콘솔 주소</th>
              <td className="mono">{originLabel()}</td>
              <th>서버 상태</th>
              <td>{readyLabel(snapshot.ready, snapshot.readyError)}</td>
            </tr>
            <tr>
              <th>서버 가동 시간</th>
              <td>{snapshot.metrics ? formatSeconds(snapshot.metrics.uptime_sec) : '—'}</td>
              <th>신원 발급자</th>
              <td className={snapshot.ready?.checks.identity_issuers.length ? 'mono' : undefined}>
                {issuersLabel(snapshot.ready)}
              </td>
            </tr>
            <tr>
              <th>등록 화면</th>
              <td>{fleetLabel(snapshot.fleet)}</td>
              <th>배정 없음</th>
              <td>{snapshot.fleet ? `${snapshot.fleet.unassigned}대` : '—'}</td>
            </tr>
            <tr>
              <th>에이전트 버전</th>
              <td colSpan={3}>{snapshot.displaysLoaded ? versionSpread(rows) : '—'}</td>
            </tr>
            <tr>
              <th>검사 수행 계정</th>
              <td className="mono">{snapshot.account}</td>
              <th>출력 일시</th>
              <td>{printedAt()}</td>
            </tr>
          </tbody>
        </table>
      </Block>

      {INSPECTION_SECTIONS.map((section, sectionIndex) => (
        <Block key={section.id} title={`${sectionIndex + 1}. ${section.title}`} hint={section.intro}>
          <table className="sheet-table sheet-items">
            <thead>
              <tr>
                <th className="col-no">번호</th>
                <th>점검 항목</th>
                <th className="col-how">확인 방법 · 합격 기준</th>
                <th className="col-verdict">판정</th>
                <th className="col-note">비고</th>
              </tr>
            </thead>
            <tbody>
              {section.items.map((item, itemIndex) => (
                <tr key={item.id}>
                  <td className="col-no">{itemNumber(sectionIndex, itemIndex)}</td>
                  <td>
                    {item.title}
                    {item.critical ? <span className="sheet-required">필수</span> : null}
                  </td>
                  <td className="col-how">
                    {item.how}
                    <span className="sheet-criteria">
                      <b>기준</b> {item.criteria}
                    </span>
                  </td>
                  <td className="col-verdict">
                    <Marks value={state.results[item.id]} />
                  </td>
                  <td className="col-note sheet-note">{state.notes[item.id] ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      ))}

      <Block
        title={`${INSPECTION_SECTIONS.length + 1}. 납품 기기 목록`}
        hint="콘솔에 등록된 화면입니다. 일련번호는 실물과 대조하며 적으세요."
      >
        {rows.length === 0 ? (
          <p className="sheet-empty">{snapshot.displaysError ?? '등록된 화면이 없습니다. 기기를 등록한 뒤 다시 출력하세요.'}</p>
        ) : (
          <table className="sheet-table sheet-devices">
            <thead>
              <tr>
                <th className="col-no">번호</th>
                <th>화면 이름</th>
                <th>설치 위치</th>
                <th className="col-res">해상도 · 방향</th>
                <th className="col-ver">에이전트</th>
                <th className="col-state">상태</th>
                <th className="col-serial">기기 일련번호</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((display, index) => (
                <DeviceRow key={display.id} index={index} display={display} />
              ))}
            </tbody>
          </table>
        )}
      </Block>

      <Block title={`${INSPECTION_SECTIONS.length + 2}. 종합 소견`} hint="불합격 항목의 조치 계획과 잔여 과제를 적습니다.">
        <div className="sheet-conclusion">{state.conclusion}</div>
      </Block>

      <table className="sheet-sign">
        <thead>
          <tr>
            <th>검사자</th>
            <th>검사 책임자</th>
            <th>고객 인수 확인</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span>성명</span>
              <span>서명</span>
            </td>
            <td>
              <span>성명</span>
              <span>서명</span>
            </td>
            <td>
              <span>성명</span>
              <span>서명</span>
            </td>
          </tr>
        </tbody>
      </table>

      <footer className="sheet-foot">
        본 성적서는 위 검사일 기준의 상태를 기록한 것입니다. 인수 후 설정 변경·기기 추가에 따른 상태는 포함하지
        않습니다.
      </footer>
    </article>
  );
}

function DeviceRow({ index, display }: { index: number; display: Display }): ReactNode {
  return (
    <tr>
      <td className="col-no">{index + 1}</td>
      <td>{display.name}</td>
      <td>{display.location || '—'}</td>
      <td className="col-res">
        {resolutionOf(display) || '—'} · {display.orientation === 'portrait' ? '세로' : '가로'}
        {display.rotation ? ` ${display.rotation}°` : ''}
      </td>
      <td className="col-ver mono">{display.status.app_version ?? '미보고'}</td>
      <td className="col-state">{stateOf(display)}</td>
      {/* 일련번호는 서버가 모른다 — 실물 스티커를 보고 손으로 적는 칸이다. */}
      <td className="col-serial" />
    </tr>
  );
}

/**
 * 판정 표시 — 세 칸을 모두 그리고 고른 칸만 채운다.
 *
 * 고른 것 하나만 적지 않는 이유: 종이에서는 칸이 검게 차 있는지로 넘겨보다가도 보이고,
 * 비어 있는 세 칸은 "판정하지 않았다" 가 그대로 드러난다.
 */
function Marks({ value }: { value: Verdict | undefined }): ReactNode {
  return (
    <div className="sheet-marks" aria-label={value ? MARK_LABEL[value] : '미판정'}>
      {(['pass', 'fail', 'na'] as const).map((verdict) => (
        <span key={verdict} className={`sheet-mark sheet-mark-${verdict} ${value === verdict ? 'is-on' : ''}`}>
          {MARK_LABEL[verdict]}
        </span>
      ))}
    </div>
  );
}

/** 적힌 글자. 비었으면 빈칸이 줄 높이를 지킨다 — 인쇄물에 손으로 채운다. */
function Text({ value, className = '' }: { value: string; className?: string }): ReactNode {
  return <span className={`sheet-text ${className}`}>{value}</span>;
}

function Block({ title, hint, children }: { title: string; hint?: string; children: ReactNode }): ReactNode {
  return (
    <section className="sheet-block">
      <h2>
        {title}
        {hint ? <span>{hint}</span> : null}
      </h2>
      {children}
    </section>
  );
}

/** 콘솔이 실제로 열려 있는 주소. 검사 성적서에는 "어느 서버를 봤는가" 가 남아야 한다. */
export function originLabel(): string {
  try {
    return window.location.origin;
  } catch {
    return '—';
  }
}
